import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticlePageLayoutProps {
  breadcrumbTitle: string;
  category: string;
  title: string;
  excerpt: string;
  readTime: string;
  heroImageUrl: string;
  heroImageAlt: string;
  schemaData: Record<string, unknown>;
  children: React.ReactNode;
}

export default function ArticlePageLayout({
  breadcrumbTitle,
  category,
  title,
  excerpt,
  readTime,
  heroImageUrl,
  heroImageAlt,
  schemaData,
  children,
}: ArticlePageLayoutProps) {
  const location = useLocation();
  const siteUrl = (import.meta as Record<string, unknown>).env as Record<string, string> | undefined;
  const siteOrigin = siteUrl?.VITE_SITE_URL || window.location.origin;
  const pageUrl = `${siteOrigin}${location.pathname}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': pageUrl,
    url: pageUrl,
    headline: title,
    description: excerpt,
    image: heroImageUrl,
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Hub',
      url: siteOrigin,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Hub',
      url: siteOrigin,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
      },
    },
    ...schemaData,
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  Home
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-foreground-400 whitespace-nowrap">{breadcrumbTitle}</span>
              </li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold whitespace-nowrap">
                {category}
              </span>
              <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold whitespace-nowrap">
                Updated 2026
              </span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{readTime}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-5">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed max-w-3xl">
              {excerpt}
            </p>
          </header>

          <div className="w-full aspect-[21/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={heroImageUrl}
              alt={heroImageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom space-y-8">
            {children}

            <section className="bg-background-100 rounded-2xl p-6 md:p-8 my-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                Ready to Start Exploring Sugargoo Spreadsheets?
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                Visit our main website to browse curated product categories. From shoes and hoodies to accessories and perfumes — organized Sugargoo Spreadsheet resources make international shopping faster, smarter and more enjoyable.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Sugargoo Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-secondary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                  <i className="ri-home-4-line"></i>
                </Link>
              </div>
            </section>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(articleSchema),
          }}
        />
      </main>
      <Footer />
    </>
  );
}